import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, Download, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);

export default function ConsultantRemuneration() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(thisMonth());
  const [editing, setEditing] = useState(null); // { id, fee, rate }

  const { data, isLoading } = useQuery({
    queryKey: ["consultants", month],
    queryFn: async () => (await api.get(`/consultants/remuneration?month=${month}`)).data,
    enabled: /^\d{4}-\d{2}$/.test(month),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["consultants"] });

  const saveFee = useMutation({
    mutationFn: async () => (await api.put(`/consultants/${editing.id}/fee`, {
      monthly_fee: Number(editing.fee || 0), tds_rate: Number(editing.rate || 0) / 100,
    })).data,
    onSuccess: () => { toast.success("Fee saved"); setEditing(null); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const setPaid = useMutation({
    mutationFn: async ({ id, paid }) => (await api.post(`/consultants/${id}/payments/${month}`, { paid })).data,
    onSuccess: () => { refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const download = async () => {
    try {
      const res = await api.get("/consultants/remuneration/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Consultant_Remuneration_${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Unable to download"); }
  };

  const rows = data?.consultants || [];
  return (
    <div>
      <PageHeader overline="Payments" title="Consultant Fees" icon={Wallet}
        subtitle="Fixed monthly remuneration for consultants (Employee Type = Consultant). TDS is under Sec 194J, or 20% if no PAN is on file."
        actions={<Button variant="outline" onClick={download} disabled={rows.length === 0}><Download className="h-4 w-4 mr-1" /> Excel</Button>} />
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-slate-500">Month</span>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
      </div>
      {isLoading ? <Loading /> : rows.length === 0 ? (
        <EmptyState icon={Wallet} title="No consultants for this month" />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Consultant</TableHead>
              <TableHead className="overline text-slate-500">Monthly Fee</TableHead>
              <TableHead className="overline text-slate-500">TDS</TableHead>
              <TableHead className="overline text-slate-500">Net Payable</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.employee_id} className="table-row-i">
                  <TableCell className="font-medium text-navy">{r.employee_name} <span className="text-slate-400 font-mono-i text-xs">({r.employee_code})</span></TableCell>
                  {editing?.id === r.employee_id ? (
                    <>
                      <TableCell colSpan={3}>
                        <div className="flex items-center gap-2">
                          <Input type="number" min="0" value={editing.fee} onChange={(e) => setEditing({ ...editing, fee: e.target.value })} className="w-32" placeholder="Fee" />
                          <Input type="number" min="0" max="100" value={editing.rate} onChange={(e) => setEditing({ ...editing, rate: e.target.value })} className="w-20" />
                          <span className="text-xs text-slate-500">% TDS</span>
                        </div>
                      </TableCell>
                      <TableCell />
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" onClick={() => saveFee.mutate()} disabled={saveFee.isPending}>Save</Button>
                        <Button size="sm" variant="outline" className="ml-2" onClick={() => setEditing(null)}>Cancel</Button>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-slate-600">{r.monthly_fee ? inr(r.monthly_fee) : <span className="text-amber-700">Not set</span>}</TableCell>
                      <TableCell className="text-slate-600">{inr(r.tds)} <span className="text-xs text-slate-400">({Math.round(r.tds_rate * 10000) / 100}%{r.has_pan ? "" : ", no PAN"})</span></TableCell>
                      <TableCell className="text-slate-800 font-medium">{inr(r.net_payable)}</TableCell>
                      <TableCell>{r.paid ? <span className="text-green-700 text-sm">Paid {r.paid_on}</span> : <span className="text-slate-500 text-sm">Pending</span>}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => setEditing({ id: r.employee_id, fee: r.monthly_fee || "", rate: Math.round(r.tds_rate * 10000) / 100 })}><Pencil className="h-3 w-3 mr-1" /> Fee</Button>
                        <Button size="sm" variant="outline" className="ml-2" onClick={() => setPaid.mutate({ id: r.employee_id, paid: !r.paid })} disabled={!r.monthly_fee}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {r.paid ? "Undo paid" : "Mark paid"}
                        </Button>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-medium">
                <TableCell>Total</TableCell>
                <TableCell>{inr(data.totals.monthly_fee)}</TableCell>
                <TableCell>{inr(data.totals.tds)}</TableCell>
                <TableCell>{inr(data.totals.net_payable)}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
